import { ArrowRightLeft, Ban, MoreVertical, CheckCircle2, ClipboardCheck, FolderLock, Gavel, History, Lock, Pencil, ScrollText, Settings, Trash2 } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { Tooltip } from '../../../../components/Tooltip';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { SearchGroup } from '../../../../components/index-page/SearchGroup';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileNav } from '../../../../components/profile/ProfileNav';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { DateCell } from '../../../../components/table/DateCell';
import { StackedCell } from '../../../../components/table/StackedCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { EvidenceEvaluateModal } from '../components/EvidenceEvaluateModal';
import { EvidenceCaseRequestsPanel } from '../components/EvidenceCaseRequestsPanel';
import { EvidenceIntakeModal } from '../components/EvidenceIntakeModal';
import { EvidenceItemModal } from '../components/EvidenceItemModal';
import { EvidenceReceiptModal } from '../components/EvidenceReceiptModal';
import { EvidenceReceiptsPanel } from '../components/EvidenceReceiptsPanel';
import { EvidenceTransferModal } from '../components/EvidenceTransferModal';
import { EvidenceContainerModal } from '../components/EvidenceContainerModal';
import { personnelLabel, usePersonnelNames } from '../../../human-resources/personnel/useStaff';
import { evidenceCaseRequestsApi, evidenceCasesApi, evidenceItemsApi } from '../evidenceApi';
import {
  ACCESS_ACTION_LABEL,
  CASE_STATUS_LABEL,
  CUSTODY,
  CUSTODY_FACET,
  CUSTODY_LABEL,
  MOVEMENT_LABEL,
  DESTINATION_LABEL,
  type EvidenceReceivingGroup,
  FLAG,
  hasFlag,
  receivedMessage,
  type EvidenceAccessLogItem,
  type EvidenceCaseListItem,
  type EvidenceContainerGroup,
  type EvidenceItemListItem,
  type EvidenceItemStatus,
} from '../evidenceTypes';

type TabKey = 'info' | 'evidence' | 'receipts';

const FLAG_NAMES: [number, string][] = [
  [FLAG.view, 'View'],
  [FLAG.share, 'Share'],
  [FLAG.upload, 'Upload'],
  [FLAG.edit, 'Edit'],
  [FLAG.delete, 'Allow delete'],
];

/**
 * One case — its facts, its evidence, who it is shared with, and who has looked.
 *
 * <b>Every control carries the KEY of what it does and is withheld or disabled by the case's own
 * FLAG.</b> The key says the account may do this kind of thing anywhere; the flag says whether
 * this case lets them. A key alone opens nothing — the server checks both.
 *
 * <b>Opening this page is LOGGED</b> server-side: a read of evidence is part of its record.
 * A case above the reader's access level is a 403, and the page says so instead of rendering.
 */
export function EvidenceCaseProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [record, setRecord] = useState<EvidenceCaseListItem | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  /*
    LANDS ON EVIDENCE — product call, 08 Oct 2026: a case is opened to work its evidence. `Info`
    is still the first tab declared; only where the page lands moves. A reader without
    `evidence-items.read` has no Evidence tab, and the strip corrects to the first one they have.
  */
  const [tab, setTab] = useState<TabKey>('evidence');
  const [modal, setModal] = useState<'access' | 'receive' | 'receipt' | 'history' | null>(null);
  const [itemsReloadKey, setItemsReloadKey] = useState(0);
  const [receiptsReloadKey, setReceiptsReloadKey] = useState(0);

  /*
    THE EVIDENCE FOLDER AND THE CUSTODY COUNTS ARE THE PAGE'S, not the Evidence tab's: the banner
    states what is waiting for evaluation on EVERY tab, and its Review action opens that folder.
    One call — the server's custody facet over the case's live items.
  */
  const canReadItems = usePermission('evidence-items.read');
  const [evidenceFolder, setEvidenceFolder] = useState<EvidenceFolder>('received');
  const [custodyCounts, setCustodyCounts] = useState<Record<string, number> | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      evidenceCasesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setRecord(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this case.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    // A different case drops what is on screen first.
    setRecord(null);
    setError(null);

    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (!id || !canReadItems) return;

    const controller = new AbortController();

    evidenceItemsApi
      .list({ caseId: id, pageSize: 1, status: 'active' }, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return;
        setCustodyCounts(Object.fromEntries((page.facets?.custody ?? []).map((f) => [f.value, f.count])));
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [id, canReadItems, itemsReloadKey]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Evidence Management',
    { label: 'Evidence', to: '/evidence/cases' },
    record?.caseNumber ?? 'Case',
  ];

  if (error && !record) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        {error.isForbidden ? (
          <EmptyState message="This case is above your access level. It is listed for you because you are a member, but it can be opened only by members cleared for its level." />
        ) : (
          <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
        )}
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const flags = record.permissions;
  const canShare = hasFlag(flags, FLAG.share);
  const waitingCount = custodyCounts?.[CUSTODY_FACET[CUSTODY.forEvaluation]] ?? 0;

  /*
    THE CASE ITSELF IS CASE MANAGEMENT'S (12 Oct 2026) — its fields, its level, its team and its
    deletion are edited there, so this menu LINKS to that record rather than repeating them. The
    ACCESS LOG stays: who read this case's evidence is Evidence's own record. Who looked is the
    managers' question: the entry is HIDDEN without the registry's Share key and DISABLED, with the
    reason, without the case's own Share flag — a key alone opens nothing here.
  */
  const openCase = () => navigate(`/cases/${record.id}`);

  const actions: MenuItem[] = [
    {
      key: 'case',
      label: 'Open case record',
      icon: <Gavel className="h-4 w-4" aria-hidden="true" />,
      permission: 'cases.read',
      onClick: openCase,
    },
    {
      key: 'access',
      label: 'Access log',
      icon: <ScrollText className="h-4 w-4" aria-hidden="true" />,
      permission: 'cases.share',
      disabledReason: hasFlag(flags, FLAG.share) ? null : 'You have not been given Share on this case.',
      onClick: () => setModal('access'),
    },
    {
      key: 'history',
      label: 'History Logs',
      icon: <History className="h-4 w-4" aria-hidden="true" />,
      permission: 'audit-logs.read',
      onClick: () => setModal('history'),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<TabKey>
        id="evidence-case-profile"
        panel={tab === 'info' ? 'cards' : 'bleed'}
        header={
          <ProfileHeader
            id="evidence-case-profile-header"
            icon={<FolderLock className="h-6 w-6" aria-hidden="true" />}
            title={record.title}
            meta={[record.caseNumber, CASE_STATUS_LABEL[record.status], record.accessLevelName ?? 'Unclassified']}
            status={
              <StatusText tone={record.isActive ? 'active' : 'inactive'}>{record.isActive ? 'Active' : 'Deleted'}</StatusText>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            actions={
              <Menu
                id="evidence-case-profile-settings"
                items={actions}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    id="evidence-case-profile-settings-trigger"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Case settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          !record.isActive ? (
            <ProfileBanner
              id="evidence-case-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This case is deleted in Case Management. Its evidence, access log and custody records are kept, and it is restored from the case record."
            />
          ) : waitingCount > 0 ? (
            /*
              WHAT IS WAITING is stated on every tab — product call, 08 Oct 2026. Review OPENS the
              folder (no confirmation: it commits nothing), and it comes off once that folder is
              already on screen — a control that navigates to where the reader is does nothing.
            */
            <ProfileBanner
              id="evidence-case-profile-banner"
              icon={<ClipboardCheck className="h-4 w-4" aria-hidden="true" />}
              message={`${waitingCount} item${waitingCount === 1 ? ' is' : 's are'} waiting for evaluation — accept each into storage, or defer it outside the facility.`}
              action={
                tab === 'evidence' && evidenceFolder === 'received' ? undefined : (
                  <Button
                    id="evidence-case-profile-review"
                    variant="secondary"
                    onClick={() => {
                      setTab('evidence');
                      setEvidenceFolder('received');
                    }}
                  >
                    Review
                  </Button>
                )
              }
            />
          ) : (
            <ProfileBanner
              id="evidence-case-profile-banner"
              icon={record.accessLevelId != null ? <Lock className="h-4 w-4" aria-hidden="true" /> : <CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={
                record.accessLevelId != null
                  ? `This case is classified ${record.accessLevelName}. Members below that level see it listed with its title masked, and cannot open it.`
                  : 'This case is unclassified. Every member it is shared with can open it.'
              }
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          // Its rows are another module's — a reader of cases alone gets no tab, not a refused fetch.
          { key: 'evidence', label: <>Evidence <CountBadge count={record.itemCount} /></>, permission: 'evidence-items.read' },
          // ACKNOWLEDGEMENT RECEIPTS — the second main tab, after Evidence (product call, 08 Oct 2026).
          // A receipt LISTS items, so the server also wants evidence-items.read — the tab asks for both.
          ...(canReadItems ? [{ key: 'receipts' as const, label: 'Acknowledgement Receipts', permission: 'evidence-receipts.read' }] : []),
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="evidence-case-profile-details"
              title="Case"
              menuItems={[
                // Another module's record — linked, never edited here.
                { key: 'case', label: 'Open case record', permission: 'cases.read', onClick: openCase },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Case number', value: record.caseNumber, mono: true },
                  { label: 'Case title', value: record.title },
                  { label: 'Case status', value: CASE_STATUS_LABEL[record.status] },
                ]}
              />
            </ProfileCard>

            <ProfileCard
              id="evidence-case-profile-access-level"
              title="Access level"
              menuItems={[
                { key: 'case', label: 'Open case record', permission: 'cases.read', onClick: openCase },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Level', value: record.accessLevelName ?? 'Unclassified' },
                  {
                    label: 'Rank',
                    value: record.accessLevelRank != null ? <span className="font-mono tabular-nums">{record.accessLevelRank}</span> : null,
                  },
                  {
                    label: 'Read by',
                    value:
                      record.accessLevelId != null
                        ? `Members tagged ${record.accessLevelName} or stronger`
                        : 'Every member of the case',
                  },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="evidence-case-profile-your-access" title="Your access">
              <PreviewTable
                variant="table-display"
                rows={FLAG_NAMES.map(([flag, name]) => ({
                  label: name,
                  value: hasFlag(flags, flag) ? 'Yes' : 'No',
                }))}
              />
            </ProfileCard>

            <ProfileCard id="evidence-case-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(record.createdAt)} className="font-mono">
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value: record.updatedAt ? (
                      <time dateTime={toIso(record.updatedAt)} className="font-mono">
                        {formatDateTime(record.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : null}

        {tab === 'evidence' ? (
          <CaseEvidence
            caseId={record.id}
            canUpload={hasFlag(flags, FLAG.upload) && record.isActive}
            moveReason={
              !record.isActive
                ? 'This case is deleted.'
                : hasFlag(flags, FLAG.edit)
                  ? null
                  : 'You have not been given Edit on this case.'
            }
            uploadReason={!record.isActive ? 'This case is deleted.' : 'You have not been given Upload on this case.'}
            reloadKey={itemsReloadKey}
            counts={custodyCounts}
            folder={evidenceFolder}
            onFolderChange={setEvidenceFolder}
            onEvaluated={() => setItemsReloadKey((k) => k + 1)}
            onReceive={() => setModal('receive')}
            onOpen={(item) => navigate(`/evidence/items/${item.id}`)}
          />
        ) : null}

        {tab === 'receipts' ? (
          // The bleed panel's inset, as the Evidence tab's — the table fills it and scrolls its own rows.
          <div className="flex h-full min-h-0 min-w-0 flex-col p-4">
            <EvidenceReceiptsPanel
              caseId={record.id}
              reloadKey={receiptsReloadKey}
              onCreate={() => setModal('receipt')}
              createDisabledReason={
                !record.isActive
                  ? 'This case is deleted.'
                  : hasFlag(flags, FLAG.upload)
                    ? null
                    : 'You have not been given Upload on this case.'
              }
            />
          </div>
        ) : null}

      </ProfileHeaderShell>

      {modal === 'receipt' ? (
        <EvidenceReceiptModal
          caseId={record.id}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            setModal(null);
            toast.success(`${saved.receiptNumber} was generated.`);
            setReceiptsReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      {modal === 'receive' ? (
        <EvidenceIntakeModal
          caseId={record.id}
          onClose={() => setModal(null)}
          onSaved={(saved) => {
            setModal(null);
            toast.success(receivedMessage(saved.receivingNumber, saved.items.map((i) => i.inventoryNumber)));
            setItemsReloadKey((k) => k + 1);
            load();
          }}
        />
      ) : null}

      {/* A read-only window, so no confirm tick and no primary action — the History Logs window's shape. */}
      {modal === 'access' && canShare ? (
        <Modal open onClose={() => setModal(null)} title="Access log" size="3xl" id="evidence-case-access-modal">
          <CaseAccessLog caseId={record.id} />
        </Modal>
      ) : null}

      {modal === 'history' ? (
        <HistoryLogsModal open onClose={() => setModal(null)} module="evidence-items" entityId={record.id} />
      ) : null}

    </div>
  );
}

type EvidenceFolder = 'requests' | 'received' | 'stored' | 'out';

/**
 * The case's evidence, in the CYCLE it moves through — product call, 08 Oct 2026: *in the evidence
 * tab, i must have nav folders: For Evaluation, Stored in, Transferred (per type)*.
 *
 * - **Received** — the FIRST stage and the default folder: what has been received and NOT YET
 *   evaluated (product call, 08 Oct 2026: *once received, stored in/deferred, remove it from the
 *   received tab*). Receive and Evaluate both live here — the separate For Evaluation folder listed
 *   the same items and was removed with it.
 * - **Stored in** — accepted, GROUPED BY CONTAINER (one accordion level, nothing to add inside).
 * - **Transferred** — everything held outside the facility, grouped by TYPE: deferred at
 *   evaluation, transferred out, disposed of. A deferred item comes back by a store-in from its own page.
 *
 * A SECOND `<FolderTabs>` strip under the profile's own — the nesting `PpaProfilePage` already
 * makes, where a separate pill component was written once and deleted as drift.
 *
 * **Receive sits INSIDE the Received folder** — product call, 08 Oct 2026, reversing the same
 * day's "one control above the folders". Receiving IS the first stage of the cycle, so it lives
 * in the folder that names that stage rather than floating over all four.
 *
 * Received is UNFILTERED: it passes no `custody`, which the server reads as every state. It is
 * deliberately not the sum of the other three — those partition what the bureau holds NOW, and
 * this one is the registration record, which a disposal does not remove.
 */
function CaseEvidence({
  caseId,
  canUpload,
  moveReason,
  uploadReason,
  reloadKey,
  counts,
  folder,
  onFolderChange,
  onEvaluated,
  onReceive,
  onOpen,
}: {
  caseId: string;
  canUpload: boolean;
  /** Why this reader cannot TRANSFER from the Stored folder (the case's Edit flag), or null. */
  moveReason: string | null;
  uploadReason: string;
  reloadKey: number;
  /** The page's custody counts — it states them in its banner too. Null until loaded. */
  counts: Record<string, number> | null;
  folder: EvidenceFolder;
  onFolderChange: (folder: EvidenceFolder) => void;
  /** An evaluation changes every folder's contents and the page's counts. */
  onEvaluated: () => void;
  onReceive: () => void;
  onOpen: (item: EvidenceItemListItem) => void;
}) {
  const toast = useToast();
  const [evaluating, setEvaluating] = useState<EvidenceItemListItem[] | null>(null);
  const canEvaluate = usePermission('evidence-items.create') && canUpload;
  const [outType, setOutType] = useState<'court' | 'deferred' | 'disposed'>('court');
  const [transferring, setTransferring] = useState(false);
  const [acknowledging, setAcknowledging] = useState<EvidenceReceivingGroup | null>(null);

  /*
    THE RECEIVED FOLDER'S HEADERS — one per receiving event (09 Oct 2026): when it was received and the
    agent who submitted it on the left, the custodian who received it on the right — the Stored in
    folder's header, one table along. The count is the event's items STILL WAITING, whatever the
    table's own search shows.
  */
  const [receivings, setReceivings] = useState<EvidenceReceivingGroup[]>([]);

  useEffect(() => {
    if (folder !== 'received') return;
    const controller = new AbortController();

    // A failed load leaves the headers without their facts; the table still lists the items.
    evidenceItemsApi
      .receivings(caseId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setReceivings(next);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [caseId, reloadKey, folder]);

  const receivingById = useMemo(() => new Map(receivings.map((r) => [r.receivingId, r])), [receivings]);
  const receivingPeople = useMemo(
    () => receivings.flatMap((r) => [r.custodianPersonnelId, r.submittingAgentPersonnelId]),
    [receivings],
  );
  const { names: receivingNames, canRead: canReadNames } = usePersonnelNames(receivingPeople);
  const unsortable = useMemo(() => ITEM_COLUMNS.map((c) => ({ ...c, sortable: false })), []);

  // DEFERRED's header — the RECEIVING TRANSACTION the item came in, and when (09 Oct 2026).
  const deferredHeader = (row: EvidenceItemListItem) => (
    <span className="truncate text-xs text-slate-500">
      <span className="font-mono">{row.receivingNumber ?? 'No receiving transaction'}</span>
      {row.receivingNumber ? (
        <>
          {' · Received '}
          <time dateTime={toIso(row.createdAt)} className="font-mono font-normal">
            {formatDateTime(row.createdAt)}
          </time>
        </>
      ) : null}
    </span>
  );

  const receivingHeader = (row: EvidenceItemListItem) => {
    const event = row.receivingId ? receivingById.get(row.receivingId) : undefined;
    const person = (id: string | null | undefined) =>
      id ? (personnelLabel(id, receivingNames, canReadNames) ?? '…') : 'several';

    return (
      <div className="flex min-w-0 items-center gap-3">
        <div className="sticky left-3 flex min-w-0 items-center gap-2">
          {/* The TRANSACTION NUMBER and the AGENT on the left (09 Oct 2026). */}
          <span className="truncate text-xs text-slate-500">
            {event?.receivingNumber ? <span className="font-mono">{event.receivingNumber}</span> : null}
            {event?.receivingNumber ? ' · ' : null}
            {/* The agent's name in CAPITALS — product call, 09 Oct 2026. Display only (CSS); the name itself is untouched. */}
            <span className="uppercase">
              {event ? (event.submittingAgentPersonnelId ? person(event.submittingAgentPersonnelId) : 'Several agents') : '…'}
            </span>
          </span>
          <CountBadge count={event?.itemCount} />
        </div>

        {event ? (
          <div className="sticky right-3 ml-auto flex shrink-0 items-center gap-2 bg-slate-50 pl-3">
            {/*
              NO LABELS — product call, 09 Oct 2026: just the name and the instant; HOVER says what they are.
              The sr-only words carry the same meaning for a reader who never hovers.
            */}
            <Tooltip label="Received by / at">
              <span tabIndex={0} className="text-xs text-slate-500">
                <span className="sr-only">Received by </span>
                {person(event.custodianPersonnelId)}
                <span className="sr-only"> at</span>
                {' · '}
                {/* MONO, like every date, at regular weight. */}
                <time dateTime={toIso(event.receivedAt)} className="font-mono font-normal">
                  {formatDateTime(event.receivedAt)}
                </time>
              </span>
            </Tooltip>
            {/*
              THE EVENT'S KEBAB, AFTER the custodian (corrected 09 Oct 2026) — product call, 09 Oct 2026: *acknowledgement receipt, that
              displays automatically the child items … locked*. Its key hides it; the case's Upload flag and an
              event with nothing left to acknowledge are STATES, so they disable with the reason.
            */}
            <Menu
              id={`evidence-case-receiving-${event.receivingId}-menu`}
              items={[
                {
                  key: 'acknowledge',
                  label: 'Acknowledgement receipt',
                  icon: <ScrollText className="h-4 w-4" aria-hidden="true" />,
                  permission: 'evidence-receipts.create',
                  disabledReason: !hasFlag(row.permissions, FLAG.upload)
                    ? 'You have not been given Upload on this case.'
                    : (event.receiptableItemIds ?? []).length === 0
                      ? 'Nothing of this receiving is waiting any more.'
                      : null,
                  onClick: () => setAcknowledging(event),
                },
              ]}
              trigger={({ open, toggle }) => (
                <button
                  type="button"
                  id={`evidence-case-receiving-${event.receivingId}-trigger`}
                  onClick={toggle}
                  aria-haspopup="menu"
                  aria-expanded={open}
                  aria-label="Receiving actions"
                  className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                >
                  <MoreVertical className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            />
          </div>
        ) : null}
      </div>
    );
  };
  const [editing, setEditing] = useState<EvidenceItemListItem | null>(null);
  const [deleting, setDeleting] = useState<EvidenceItemListItem | null>(null);
  const [deleteError, setDeleteError] = useState<ApiError | null>(null);
  const [running, setRunning] = useState(false);

  async function runDelete(reauthentication?: string) {
    if (!deleting) return;

    setRunning(true);
    setDeleteError(null);

    try {
      await evidenceItemsApi.deactivate(deleting.id, reauthentication ?? '');
      toast.success(`${deleting.inventoryNumber} was deleted.`);
      setDeleting(null);
      onEvaluated();
    } catch (caught) {
      setDeleteError(caught instanceof ApiError ? caught : new ApiError('The item could not be deleted.', 0));
    } finally {
      setRunning(false);
    }
  }

  /*
    THE RECEIVED ROW'S MENU — product call, 08 Oct 2026: evaluate, edit, delete, in that order
    (the two status updates were removed with their fields). Each carries the KEY of what it does (hidden
    without it) and is DISABLED, with the reason, for the case's own flag or the item's state.
    Edit rides as an extra rather than `onEdit` because only an extra can say why it is off; the
    menu's fixed order still puts it right before Delete.
  */
  const receivedActions = (row: EvidenceItemListItem): MenuItem[] => {
    const editReason = !row.isActive
      ? 'This item is deleted.'
      : !hasFlag(row.permissions, FLAG.edit)
        ? 'You have not been given Edit on its case.'
        : null;

    /*
      EVALUATE IS NOT OFFERED on a stored, transferred or disposed item — product call, 08 Oct 2026:
      *evaluate is not applicable for stored and transferred*. It is not a state the item can leave
      (which would disable with a reason); the action does not apply to it at all.
    */
    // ONLY what WAITS (09 Oct 2026, reversing "a deferral may be accepted later"; a deferred item comes back by Store-in).
    const evaluable = row.status === CUSTODY.forEvaluation;

    return [
      ...(evaluable
        ? [
            {
              key: 'evaluate',
              label: 'Evaluate',
              icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
              permission: 'evidence-items.create',
              disabledReason: !row.isActive ? 'This item is deleted.' : evaluateReason,
              onClick: () => setEvaluating([row]),
            },
          ]
        : []),
      {
        key: 'edit',
        label: 'Edit',
        icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
        permission: 'evidence-items.update',
        disabledReason: editReason,
        onClick: () => setEditing(row),
      },
    ];
  };

  /*
    EVERY FOLDER'S TABLE IS THE RECEIVED TABLE — product call, 08 Oct 2026: *the for evaluation,
    stored in, transferred should have same column as received, unless i specify difference*.
    Same columns, same tint, same row menu, spread into each — one object, so a folder cannot
    drift from the others by a prop somebody forgot.
  */
  const rowProps = {
    columns: ITEM_COLUMNS,
    // NO red tint on a waiting row — product call, 08 Oct 2026: removed; the banner says what waits.
    // ONLY EVALUATE is promoted beside the kebab — product call, 09 Oct 2026 (*only expose the evaluate*).
    // Moved, never duplicated: Edit stays in the kebab with Other Info and Delete.
    rowQuickActions: (row: EvidenceItemListItem) => receivedActions(row).filter((a) => a.key === 'evaluate'),
    rowActions: (row: EvidenceItemListItem) => receivedActions(row).filter((a) => a.key !== 'evaluate'),
    onDelete: (row: EvidenceItemListItem) => setDeleting(row),
    deletePermission: 'evidence-items.delete',
    deleteDisabledReason: (row: EvidenceItemListItem) => deleteReason(row),
  };

  const deleteReason = (row: EvidenceItemListItem): string | null =>
    !row.isActive
      ? 'This item is already deleted.'
      : !hasFlag(row.permissions, FLAG.delete)
        ? 'You have not been given Allow delete on its case.'
        : row.pendingMovementType != null
          ? 'A movement of this item is waiting for approval.'
          : row.receiptNumber != null
            ? `This evidence is acknowledged on receipt ${row.receiptNumber}, which cannot be changed.`
            : row.status === CUSTODY.stored && row.movementCount > 1
            ? 'This evidence is in storage. It leaves by a transfer or a disposal, not a delete.'
            : row.status === CUSTODY.deferred && row.movementCount > 1
              ? 'This evidence was deferred and is held outside the facility. It is not removed by a delete.'
              : null;

  const key = reloadKey;

  const count = (status: EvidenceItemStatus) => counts?.[CUSTODY_FACET[status]];
  // Received is what WAITS — once evaluated, an item leaves it for Stored in or Transferred.
  const receivedCount = count(CUSTODY.forEvaluation) ?? (counts ? 0 : undefined);

  /*
    REQUESTS WAITING — product call, 09 Oct 2026: Self Service's evidence requests, in a folder ABOVE
    Received. The rail's count is the server's total of PENDING requests, from the folder's own endpoint
    (one row asked for, the total kept), so it agrees with the table whether or not the folder is open.
  */
  const [requestCount, setRequestCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    const controller = new AbortController();

    // A failed count leaves the badge off — the folder still lists what waits when opened.
    evidenceCaseRequestsApi
      .list(caseId, { pageSize: 1 }, controller.signal)
      .then((page) => {
        if (!controller.signal.aborted) setRequestCount(page.total);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [caseId, reloadKey]);

  const evaluateReason = canUpload ? null : uploadReason;

  const evaluateAction = (rows: EvidenceItemListItem[]): MenuItem[] => [
    {
      key: 'evaluate',
      label: rows.length > 1 ? `Evaluate ${rows.length} items` : 'Evaluate',
      icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
      permission: 'evidence-items.create',
      disabledReason: evaluateReason,
      onClick: () => setEvaluating(rows),
    },
  ];

  return (
    /*
      THE TAB FILLS THE PANEL AND THE CHILD TABLE SCROLLS — product call, 08 Oct 2026: *remove the
      overflowscroll from the evidence tab, instead the overflow should be inside the child table*.
      A height chain from the bleed panel down: the folder box takes what is left, the table
      (`fill`) or the accordion region takes what the box leaves, and only that scrolls.
    */
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-3 p-4">
      {/*
        A SIMPLE VERTICAL FILTER ON THE LEFT — product call, 09 Oct 2026: *instead of nav filter, make it simple
        vertical filter on the left*. ONE rail for every folder — Received, Stored in, and Transferred as a group
        of its three destinations — so the Transferred folder no longer carries a second rail of its own. Counts
        render even at zero (`<ProfileNav>`). The table beside it scrolls; the rail scrolls on its own.
      */}
      <div className="flex min-h-0 flex-1 gap-3">
        <aside className="modern-scrollbar w-52 shrink-0 overflow-y-auto overflow-x-hidden">
          <ProfileNav
            id="evidence-case-folders"
            activeKey={folder === 'out' ? outType : folder}
            onSelect={(next) => {
              const out = OUT_TYPES.find((t) => t.key === next);
              if (out) {
                setOutType(out.key);
                onFolderChange('out');
              } else {
                onFolderChange(next as EvidenceFolder);
              }
            }}
            entries={[
              { key: 'requests', label: 'Requests', count: requestCount },
              // A RULE between Requests and the folders — product call, 09 Oct 2026.
              { key: 'received', label: 'Received', count: receivedCount, dividerBefore: true },
              { key: 'stored', label: 'Stored in', count: count(CUSTODY.stored) ?? (counts ? 0 : undefined) },
              {
                key: 'transferred',
                label: 'Transferred',
                defaultOpen: true,
                items: OUT_TYPES.map(({ key: typeKey, status, label }) => ({
                  key: typeKey,
                  label,
                  count: count(status) ?? (counts ? 0 : undefined),
                })),
              },
            ]}
          />
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {folder === 'requests' ? (
            // WHAT EMPLOYEES ASKED THE TEAM TO DO — each row opens the real Receive or Transfer, pre-filled.
            <EvidenceCaseRequestsPanel
              caseId={caseId}
              reloadKey={key}
              uploadReason={canUpload ? null : uploadReason}
              moveReason={moveReason}
              onChanged={onEvaluated}
            />
          ) : null}

          {folder === 'received' ? (
            /*
              WHAT WAITS FOR EVALUATION. Receive is the TABLE's Create, in its toolbar's leading
              slot; the ROW opens the Evaluate form (a reader who cannot evaluate — no key, or no
              Upload on the case — gets the item page instead, so the anchor is never a dead
              click); ticking several evaluates them together.
            */
            <CaseItems
              id="evidence-case-received"
              caseId={caseId}
              custody={[CUSTODY.forEvaluation]}
              // ONE header per RECEIVING EVENT — product call, 09 Oct 2026: *group also the evidence item per
              // receive batch transaction*. The server orders event by event, so the columns do not sort.
              groupByReceiving
              groupRow={{ key: (row) => row.receivingId ?? '', render: receivingHeader }}
              reloadKey={key}
              onOpen={(row) => (canEvaluate ? setEvaluating([row]) : onOpen(row))}
              fill
              {...rowProps}
              columns={unsortable}
              bulkActions={evaluateAction}
              onCreate={onReceive}
              createLabel="Receive"
              createPermission="evidence-items.create"
              createDisabledReason={canUpload ? null : uploadReason}
              emptyMessage="Nothing is waiting for evaluation. Received evidence appears here until it is accepted or deferred."
            />
          ) : null}

          {folder === 'stored' ? (
            <StoredByContainer
              caseId={caseId}
              reloadKey={key}
              onOpen={onOpen}
              rowProps={rowProps}
              onTransfer={() => setTransferring(true)}
              transferReason={moveReason}
              onChanged={onEvaluated}
            />
          ) : null}

          {folder === 'out' ? (
            /*
              The destination picked in the rail's Transferred group — the same columns and menu as every
              other folder, plus Transfer detail.
            */
                <CaseItems
                  key={outType}
                  id={`evidence-case-${outType}`}
                  caseId={caseId}
                  custody={[OUT_STATUS[outType]]}
                  reloadKey={key}
                  onOpen={onOpen}
                  fill
                  {...rowProps}
                  columns={OUT_COLUMNS}
                  // DEFERRED runs receiving transaction by receiving transaction (09 Oct 2026), so each item
                  // is read as part of the receive it came in.
                  groupByReceiving={outType === 'deferred'}
                  groupRow={outType === 'deferred' ? { key: (row) => row.receivingId ?? '', render: deferredHeader } : undefined}
                  emptyMessage={OUT_TYPES.find((t) => t.key === outType)?.empty ?? 'Nothing here.'}
                />
          ) : null}
        </div>
      </div>

      {editing ? (
        <EvidenceItemModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast.success(`${editing.inventoryNumber} was saved.`);
            setEditing(null);
            onEvaluated();
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          open
          onClose={() => {
            setDeleting(null);
            setDeleteError(null);
          }}
          onConfirm={(reauthentication) => void runDelete(reauthentication)}
          title="Delete evidence item"
          danger
          requireConfirm
          requireCredential
          loading={running}
          id="evidence-case-item-delete-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                {deleting.inventoryNumber} stops appearing on this case. Its chain of custody, documents and
                files are kept, and it can be restored.
              </p>
              {deleteError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {deleteError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      {acknowledging ? (
        <EvidenceReceiptModal
          caseId={caseId}
          preset={{
            itemIds: acknowledging.receiptableItemIds ?? [],
            receivedFromPersonnelId: acknowledging.submittingAgentPersonnelId,
            receivedByPersonnelId: acknowledging.custodianPersonnelId,
            receivedAt: acknowledging.receivedAt,
          }}
          onClose={() => setAcknowledging(null)}
          onSaved={(saved) => {
            setAcknowledging(null);
            toast.success(`${saved.receiptNumber} was generated.`);
            onEvaluated();
          }}
        />
      ) : null}

      {transferring ? (
        <EvidenceTransferModal
          caseId={caseId}
          onClose={() => setTransferring(false)}
          onSaved={(result, destination) => {
            setTransferring(false);
            toast.success(
              result.pending
                ? `${result.itemCount} item${result.itemCount === 1 ? '' : 's'} sent for approval — ${DESTINATION_LABEL[destination].toLowerCase()}. Nothing moves until it is approved.`
                : `${result.itemCount} item${result.itemCount === 1 ? ' was' : 's were'} deferred to other storage.`,
            );
            onEvaluated();
          }}
        />
      ) : null}

      {evaluating ? (
        <EvidenceEvaluateModal
          items={evaluating}
          onClose={() => setEvaluating(null)}
          onSaved={(evaluated) => {
            toast.success(
              evaluated.length === 1 ? `${evaluated[0].inventoryNumber} was evaluated.` : `${evaluated.length} items were evaluated.`,
            );
            setEvaluating(null);
            onEvaluated();
          }}
        />
      ) : null}
    </div>
  );
}

/** The Transferred folder's types — everything held outside the facility, in the rail's order. */
/*
  COURT · DEFERRED · DISPOSED — product call, 08 Oct 2026: *remove the Transferred filter*; the rail
  names the Transfer form's destinations. "Court" is the TRANSFERRED custody state — a court is the
  only place a transfer goes now (the item form keeps change of storage only).
*/
const OUT_TYPES = [
  { key: 'court', status: CUSTODY.transferred, label: 'Court', empty: 'Nothing has been sent to court.' },
  { key: 'deferred', status: CUSTODY.deferred, label: 'Deferred', empty: 'Nothing is deferred.' },
  { key: 'disposed', status: CUSTODY.disposed, label: 'Disposed', empty: 'Nothing has been disposed of.' },
] as const;

const OUT_STATUS: Record<(typeof OUT_TYPES)[number]['key'], EvidenceItemStatus> = {
  court: CUSTODY.transferred,
  deferred: CUSTODY.deferred,
  disposed: CUSTODY.disposed,
};

/**
 * The Stored in folder — product call, 08 Oct 2026: *remove the accordion panel, merge all evidence
 * in 1 table, instead insert a header per container group*, with a kebab at the header's outer end:
 * Edit (the box's custodian and agent) and Delete (the box and its items). ONE table, ordered box by
 * box by the server (so its columns do not sort — another order would split a box); the headers are
 * `<DataTable groupRow>`, facts from `GET …/containers` so a box's count is the whole box, not the page.
 * Only the box's CUSTODIAN may edit or delete it — a state of the account, so the entries disable
 * with the reason rather than hide.
 */
function StoredByContainer({
  caseId,
  reloadKey,
  onOpen,
  rowProps,
  onTransfer,
  transferReason,
  onChanged,
}: {
  caseId: string;
  reloadKey: number;
  onOpen: (item: EvidenceItemListItem) => void;
  onTransfer: () => void;
  /** Why the reader cannot transfer from this case, or null. A STATE — disabled with the reason. */
  transferReason: string | null;
  /** A box was edited or deleted — every folder's contents and the page's counts move. */
  onChanged: () => void;
  /** The Received table's columns, tint and row menu — every folder shares them. */
  rowProps: Pick<
    Parameters<typeof CaseItems>[0],
    'columns' | 'rowTone' | 'rowQuickActions' | 'rowActions' | 'onDelete' | 'deletePermission' | 'deleteDisabledReason'
  >;
}) {
  const toast = useToast();
  const [groups, setGroups] = useState<EvidenceContainerGroup[]>([]);
  const [editing, setEditing] = useState<EvidenceContainerGroup | null>(null);
  const [deleting, setDeleting] = useState<EvidenceContainerGroup | null>(null);
  const [deleteError, setDeleteError] = useState<ApiError | null>(null);
  const [acting, setActing] = useState(false);

  /*
    ONE SEARCH FOR THE WHOLE FOLDER — product call, 08 Oct 2026: it narrows the table and the box
    facts alike (both read the list's own search), so a header's count and its rows cannot disagree.
  */
  const [search, setSearch] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    // A failed load leaves the headers without their facts; the table still lists the items.
    evidenceItemsApi
      .containers(caseId, search, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setGroups(next);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [caseId, reloadKey, search]);

  const byKey = useMemo(() => new Map(groups.map((g) => [g.containerNumber ?? '', g])), [groups]);
  const personnelIds = useMemo(
    () => groups.flatMap((g) => [g.custodianPersonnelId, g.submittingAgentPersonnelId]),
    [groups],
  );
  const { names, canRead } = usePersonnelNames(personnelIds);

  // The server orders box by box; a column sort would split a box, so none is offered.
  const columns = useMemo(() => (rowProps.columns ?? ITEM_COLUMNS).map((c) => ({ ...c, sortable: false })), [rowProps.columns]);

  async function deleteBox(reauthentication?: string) {
    if (!deleting || !reauthentication) return;

    setActing(true);
    setDeleteError(null);

    try {
      await evidenceItemsApi.deleteContainer(caseId, deleting.containerNumber, reauthentication);
      toast.success(`Container ${deleting.containerNumber ?? '(no container)'} and its ${deleting.itemCount === 1 ? 'item were' : `${deleting.itemCount} items were`} deleted.`);
      setDeleting(null);
      onChanged();
    } catch (caught) {
      setDeleteError(caught instanceof ApiError ? caught : new ApiError('Could not delete the container.', 0));
    } finally {
      setActing(false);
    }
  }

  const header = (row: EvidenceItemListItem) => {
    const box = byKey.get(row.containerNumber ?? '');
    const notHolder = box?.canManage ? null : "Only the container's custodian can change or delete it.";

    return (
      /*
        THE BOX · ITS AGENT on the left, the CUSTODIAN and the kebab on the right — product call,
        08 Oct 2026: *remove the container/box. place the custodian name at the left of the kebab.
        concat the name of the box + agent name. freeze the header kebab as well*. Both halves are
        STICKY inside the full-width cell, so a table scrolled sideways keeps the box named and its
        kebab in reach — the rows' own Actions gutter is pinned the same way.
      */
      <div className="flex min-w-0 items-center gap-3">
        <div className="sticky left-3 flex min-w-0 items-center gap-2">
          {/* SMALLER, LIGHTER, the agent in CAPITALS — product call, 09 Oct 2026; the Received header's treatment. */}
          <span className="truncate text-xs text-slate-500">
            <span className="font-mono">{row.containerNumber ?? 'No container/box'}</span>
            {/* The RECEIVING TRANSACTIONS its items came in (09 Oct 2026) — a box may hold several. */}
            {box?.receivingNumbers && box.receivingNumbers.length > 0 ? (
              <span className="font-mono">{' · '}{box.receivingNumbers.join(', ')}</span>
            ) : null}
            {box ? (
              <>
                {' · '}
                <span className="uppercase">
                  {box.submittingAgentPersonnelId
                    ? (personnelLabel(box.submittingAgentPersonnelId, names, canRead) ?? '…')
                    : 'several agents'}
                </span>
              </>
            ) : null}
          </span>
          <CountBadge count={box?.itemCount} />
        </div>

        {box ? (
          <div className="sticky right-3 ml-auto flex shrink-0 items-center gap-2 bg-slate-50 pl-3">
            <span className="text-xs text-slate-500">
              Custodian:{' '}
              {box.custodianPersonnelId ? (personnelLabel(box.custodianPersonnelId, names, canRead) ?? '…') : 'several'}
            </span>

          <Menu
            id={`evidence-case-container-${row.containerNumber ?? 'none'}-menu`}
            items={[
              {
                key: 'edit',
                label: 'Edit',
                icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
                permission: 'evidence-items.update',
                disabledReason: !hasFlag(row.permissions, FLAG.edit) ? 'You have not been given Edit on this case.' : notHolder,
                onClick: () => setEditing(box),
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
                permission: 'evidence-items.delete',
                destructive: true,
                disabledReason: !hasFlag(row.permissions, FLAG.delete)
                  ? 'You have not been given Allow delete on this case.'
                  : (notHolder ?? box.deleteBlockedReason ?? null),
                onClick: () => setDeleting(box),
              },
            ]}
            trigger={({ open, toggle }) => (
              <button
                type="button"
                id={`evidence-case-container-${row.containerNumber ?? 'none'}-trigger`}
                onClick={toggle}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Container ${row.containerNumber ?? '(no container)'} actions`}
                className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
              >
                <MoreVertical className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      {/*
        IN A ROW, as `<DataTablePanel>` holds it: `<SearchGroup>` is `flex-1` to fill a TOOLBAR ROW, and
        dropped straight into this flex COLUMN that grow turned into height.
      */}
      <div className="flex min-w-0 shrink-0 items-center gap-2">
        {/*
          TRANSFER on the search row — product call, 08 Oct 2026: to a court, to disposal, or to
          storage outside. In the leading slot Create holds on a list, because it is this folder's
          one act. The KEY hides it; the case's Edit flag is a state, so it disables with the reason.
        */}
        <PermissionGate permission="evidence-items.move">
          <Tooltip disabledReason={transferReason}>
            <Button
              id="evidence-case-stored-transfer"
              variant="primary"
              leading={<ArrowRightLeft className="h-4 w-4" aria-hidden="true" />}
              onClick={onTransfer}
              disabled={transferReason !== null}
            >
              Transfer
            </Button>
          </Tooltip>
        </PermissionGate>

        <SearchGroup
          id="evidence-case-stored-search"
          value={search}
          onChange={setSearch}
          placeholder="Search the stored evidence"
          total={groups.reduce((sum, g) => sum + g.itemCount, 0)}
        />
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <CaseItems
          id="evidence-case-stored"
          caseId={caseId}
          custody={[CUSTODY.stored]}
          groupByContainer
          groupRow={{ key: (row) => row.containerNumber ?? '', render: header }}
          search={search}
          searchable={false}
          reloadKey={reloadKey}
          onOpen={onOpen}
          fill
          {...rowProps}
          columns={columns}
          emptyMessage={search.trim() ? 'No stored evidence matches that search.' : 'Nothing has been accepted into storage yet.'}
        />
      </div>

      {editing ? (
        <EvidenceContainerModal
          caseId={caseId}
          container={editing}
          itemCount={editing.itemCount}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast.success(`Container ${editing.containerNumber ?? '(no container)'} was saved.`);
            setEditing(null);
            onChanged();
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          open
          onClose={() => {
            setDeleting(null);
            setDeleteError(null);
          }}
          onConfirm={(reauthentication) => void deleteBox(reauthentication)}
          title="Delete container"
          danger
          requireConfirm
          requireCredential
          loading={acting}
          id="evidence-case-container-delete-dialog"
          body={
            <div className="flex flex-col gap-2">
              <p>
                Container {deleting.containerNumber ?? '(no container)'} and{' '}
                {deleting.itemCount === 1 ? 'its 1 item' : `its ${deleting.itemCount} items`} are deleted: they leave the
                case's folders, and each item's chain of custody is kept. A deleted item can be restored from its own page.
              </p>
              {deleteError ? <p className="text-red-700">{deleteError.message}</p> : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}

/**
 * The names on the page on screen, resolved ONCE by `CaseItems` — the PII seam's batch rule — and
 * read by each "Acquired by" cell. A context, because the column list is shared module state.
 */
const PageNamesContext = createContext<{ names: Record<string, string>; canRead: boolean }>({ names: {}, canRead: false });

function AcquiredByCell({ personnelId }: { personnelId: string | null }) {
  const { names, canRead } = useContext(PageNamesContext);
  return <TextCell value={personnelLabel(personnelId, names, canRead)} />;
}

/** One slice of the case's items — the server's list, scoped to the case, custody and container. */
function CaseItems({
  id,
  caseId,
  custody,
  groupByContainer = false,
  groupByReceiving = false,
  groupRow,
  container,
  search,
  searchable = true,
  columns = ITEM_COLUMNS,
  rowTone,
  onDelete,
  deletePermission,
  deleteDisabledReason,
  fill = false,
  reloadKey,
  onOpen,
  bulkActions,
  rowActions,
  rowQuickActions,
  onCreate,
  createLabel,
  createPermission,
  createDisabledReason,
  emptyMessage,
}: {
  id: string;
  caseId: string;
  /** Box by box — the Stored in folder's one table. */
  groupByContainer?: boolean;
  /** Receiving event by event — the Received folder's table. */
  groupByReceiving?: boolean;
  /** Its header rows, one per box. */
  groupRow?: { key: (row: EvidenceItemListItem) => string; render: (row: EvidenceItemListItem) => ReactNode };
  /** OMITTED means every custody state — the server reads an absent facet as no constraint. */
  custody?: EvidenceItemStatus[];
  container?: string;
  /** A search the CALLER owns — the Stored in folder's one search over every group. */
  search?: string;
  /** False when `search` covers this table, so it draws no search of its own. */
  searchable?: boolean;
  columns?: DataColumn<EvidenceItemListItem>[];
  rowTone?: (row: EvidenceItemListItem) => 'red' | null;
  onDelete?: (row: EvidenceItemListItem) => void;
  deletePermission?: string;
  deleteDisabledReason?: (row: EvidenceItemListItem) => string | null;
  /** The table takes the height it is given and scrolls its own rows. */
  fill?: boolean;
  reloadKey: number;
  onOpen: (item: EvidenceItemListItem) => void;
  bulkActions?: (rows: EvidenceItemListItem[]) => MenuItem[];
  rowActions?: (row: EvidenceItemListItem) => MenuItem[];
  /** Icon buttons beside the kebab — the row's Evaluate and Edit. */
  rowQuickActions?: (row: EvidenceItemListItem) => MenuItem[];
  /** Only the Received folder creates — the others are views of what was received. */
  onCreate?: () => void;
  createLabel?: string;
  createPermission?: string;
  createDisabledReason?: string | null;
  emptyMessage: string;
}) {
  const custodyParam = custody?.map((c) => CUSTODY_FACET[c]).join(',');

  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) =>
      evidenceItemsApi.list(
        {
          ...query,
          search: search?.trim() ? search : query.search,
          caseId,
          custody: custodyParam,
          container,
          groupByContainer,
          groupByReceiving,
          sort: query.sort ?? undefined,
        },
        signal,
      ),
    // reloadKey re-creates the callback, which is what makes the table refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseId, custodyParam, container, search, reloadKey, groupByContainer, groupByReceiving],
  );

  const state = useDataTable<EvidenceItemListItem>({ fetchPage, pageSize: 25 });

  const acquirers = useMemo(() => state.rows.map((r) => r.agentPersonnelId), [state.rows]);
  const pageNames = usePersonnelNames(acquirers);

  return (
    <PageNamesContext.Provider value={pageNames}>
    <DataTablePanel
      id={id}
      state={state}
      columns={columns}
      rowKey={(row) => row.id}
      searchPlaceholder="Search this evidence"
      fill={fill}
      rowTone={rowTone}
      groupRow={groupRow}
      onDelete={onDelete}
      deletePermission={deletePermission}
      deleteDisabledReason={deleteDisabledReason}
      searchable={searchable}
      onRowOpen={onOpen}
      openPermission="evidence-items.read"
      bulkActions={bulkActions}
      rowActions={rowActions}
      rowQuickActions={rowQuickActions}
      onCreate={onCreate}
      createLabel={createLabel}
      createPermission={createPermission}
      createDisabledReason={createDisabledReason}
      otherInfo={(row) => row}
      emptyMessage={emptyMessage}
      emptySearchMessage="No evidence here matches that search."
    />
    </PageNamesContext.Provider>
  );
}

/*
  EVERY FOLDER'S COLUMNS — the Received folder's, product call 08 Oct 2026: the description and the
  inventory number MERGED, the number under it; custody RIGHT AFTER, not beside status. A stacked anchor is
  legal on a `<DataTablePanel>` (never an index table), and custody here is a plain column: in a
  folder that lists every state, where an item stands is the second thing read, not the last.
*/
/*
  TYPE FIRST, then the description with the inventory no. and the storage location under it —
  product call, 08 Oct 2026: *transfer the type column before the description. merge the storage
  to description, concat the storage after the inventory number*. The first column is the row's
  click target, so Type now opens the item.
*/
const ITEM_COLUMNS: DataColumn<EvidenceItemListItem>[] = [
  { key: 'type', header: 'Type', width: 150, sortable: true, render: (row) => <TextCell value={row.typeName} /> },
  {
    key: 'description',
    header: 'Description',
    width: 340,
    summary: true,
    render: (row) => (
      <StackedCell
        primary={row.description}
        secondary={row.storageLocationName ? `${row.inventoryNumber} · ${row.storageLocationName}` : row.inventoryNumber}
        mono
      />
    ),
  },
  {
    key: 'custody',
    header: 'Custody',
    width: 200,
    /*
      RED while it waits for evaluation — product call, 09 Oct 2026; every other state is the neutral pill.
      A SECOND, amber pill while a movement waits for APPROVAL (10 Oct 2026, asked for transfers; a pending
      change of storage waits the same way, so it shows too) — the tooltip names which. Absent on a restricted
      row: the server withholds the pending type there.
    */
    render: (row) => (
      <span className="inline-flex flex-wrap items-center gap-1">
        <StatusText tone={row.status === CUSTODY.forEvaluation ? 'blocked' : 'inactive'}>{CUSTODY_LABEL[row.status]}</StatusText>
        {row.pendingMovementType != null ? (
          <Tooltip label={`${MOVEMENT_LABEL[row.pendingMovementType]}, waiting for approval`}>
            <StatusText tone="pending">for approval</StatusText>
          </Tooltip>
        ) : null}
      </span>
    ),
  },
  // ACQUIRED BY before Acquired — product call, 09 Oct 2026. The name is resolved once per page (`CaseItems`).
  { key: 'acquiredBy', header: 'Acquired by', width: 200, render: (row) => <AcquiredByCell personnelId={row.agentPersonnelId} /> },
  { key: 'acquiredAt', header: 'Acquired', width: 200, sortable: true, render: (row) => <DateCell value={row.acquiredAt} /> },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) => (row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>),
  },
];

/*
  THE TRANSFERRED FOLDER'S TABLE — every other folder's, with TRANSFER DETAIL after CUSTODY (it sat
  after the exhibit no., which followed custody until that field was removed, 08 Oct 2026).
*/
const OUT_COLUMNS: DataColumn<EvidenceItemListItem>[] = ITEM_COLUMNS.flatMap((column) =>
  column.key === 'custody'
    ? [
        column,
        {
          key: 'transferDetail',
          header: 'Transfer detail',
          width: 260,
          render: (row: EvidenceItemListItem) =>
            row.transferredTo == null && row.transferReason == null ? (
              <TextCell value={null} />
            ) : (
              <StackedCell primary={row.transferredTo ?? row.transferReason} secondary={row.transferredTo ? row.transferReason : null} />
            ),
        },
      ]
    : [column],
);

/** Who read this case's evidence, newest first. Server-paged — it grows with every read. */
function CaseAccessLog({ caseId }: { caseId: string }) {
  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) =>
      evidenceCasesApi.accessLog(caseId, { search: query.search, page: query.page, pageSize: query.pageSize }, signal),
    [caseId],
  );

  const state = useDataTable<EvidenceAccessLogItem>({ fetchPage, pageSize: 25 });

  return (
    <div className="flex min-w-0 flex-col">
      <DataTablePanel
        id="evidence-case-access-log"
        state={state}
        columns={ACCESS_COLUMNS}
        rowKey={(row) => row.id}
        searchPlaceholder="Search who, inventory number, file or address"
        selectable={false}
        auditColumns={false}
        emptyMessage="Nobody has opened this case's evidence yet."
        emptySearchMessage="No entry matches that search."
      />
    </div>
  );
}

const ACCESS_COLUMNS: DataColumn<EvidenceAccessLogItem>[] = [
  { key: 'createdAt', header: 'When', width: 200, render: (row) => <DateCell value={row.createdAt} /> },
  { key: 'createdByName', header: 'Who', width: 200, render: (row) => <TextCell value={row.createdByName} /> },
  { key: 'action', header: 'What', width: 300, summary: true, render: (row) => <TextCell value={ACCESS_ACTION_LABEL[row.action] ?? null} /> },
  { key: 'inventoryNumber', header: 'Inventory No.', width: 170, render: (row) => <TextCell value={row.inventoryNumber} mono /> },
  { key: 'detail', header: 'File', width: 220, render: (row) => <TextCell value={row.detail} /> },
  { key: 'clientIp', header: 'From', width: 150, render: (row) => <TextCell value={row.clientIp} mono /> },
];
